import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Instagram, Mail } from "lucide-react";

const PROVINCES = ["", "Bruxelles-Capitale", "Liège", "Hainaut", "Namur", "Brabant wallon", "Luxembourg"];

const EMPTY = { name: "", instagram_url: "", province: "", city: "", school: "", description: "", contact_email: "", address: "" };

export default function LocalGroupsTab() {
  const [groups, setGroups] = React.useState([]);
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY);

  const load = React.useCallback(() => {
    api.get("/admin/local-groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/local-groups/${editing}`, form);
      else await api.post("/admin/local-groups", form);
      toast.success(editing ? "Groupe mis à jour" : "Groupe créé");
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const approve = async (g) => {
    await api.patch(`/admin/local-groups/${g.id}`, { approved: true });
    toast.success(`"${g.name}" publié sur la carte 🎉`);
    load();
  };

  const edit = (g) => {
    setEditing(g.id);
    setForm({
      name: g.name || "",
      instagram_url: g.instagram_url || "",
      province: g.province || "",
      city: g.city || "",
      school: g.school || "",
      description: g.description || "",
      contact_email: g.contact_email || "",
      address: g.address || "",
    });
    setShowForm(true);
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce groupe local ?")) return;
    await api.delete(`/admin/local-groups/${id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Groupes locaux ({groups.length})</h2>
        <button
          onClick={() => { setShowForm((s) => !s); setEditing(null); setForm(EMPTY); }}
          className="neo-btn bg-cef-blue text-white"
          data-testid="new-local-group-btn"
        >
          <Plus className="w-4 h-4" /> Nouveau groupe
        </button>
      </div>

      <p className="text-sm text-neutral-600 mb-3">
        Les groupes locaux sont affichés sur la page <em>Carte</em>, dans la section « Groupes d'élèves locaux » sous l'agenda. Chaque groupe peut être suivi sur Instagram et contacté par email.
      </p>

      {showForm && (
        <form onSubmit={submit} className="neo-card p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="local-group-form">
          <input className="neo-input sm:col-span-2" placeholder="Nom du groupe (ex: CEF Bruxelles) *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="lg-name" />
          <input className="neo-input" placeholder="Lien Instagram (https://…)" value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} data-testid="lg-instagram" />
          <input className="neo-input" placeholder="Email de contact" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="lg-email" />
          <select className="neo-input" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="lg-province">
            {PROVINCES.map((p) => <option key={p} value={p}>{p || "— Province —"}</option>)}
          </select>
          <input className="neo-input" placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="lg-city" />
          <input className="neo-input sm:col-span-2" placeholder="École (optionnel)" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} data-testid="lg-school" />
          <input className="neo-input sm:col-span-2" placeholder="Adresse (rue, n° — pour la géolocalisation auto)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="lg-address" />
          <p className="text-xs text-neutral-500 sm:col-span-2 -mt-1">💡 L'adresse est utilisée pour placer le marqueur sur la carte (OpenStreetMap). Plus elle est précise (rue + n° + ville), plus c'est juste.</p>
          <textarea className="neo-input sm:col-span-2 min-h-[80px]" placeholder="Description courte" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="lg-description" />
          <button type="submit" className="neo-btn bg-cef-pink text-white sm:col-span-2" data-testid="lg-submit">{editing ? "Enregistrer" : "Créer le groupe"}</button>
        </form>
      )}

      <div className="grid gap-3" data-testid="local-groups-list">
        {groups.length === 0 && <div className="neo-card p-5 text-neutral-500">Aucun groupe local pour le moment.</div>}
        {groups.map((g) => {
          const pending = g.approved === false;
          return (
            <div key={g.id} className={`neo-card p-4 flex items-start justify-between gap-3 flex-wrap ${pending ? "bg-cef-yellow" : ""}`} data-testid={`admin-lg-${g.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-bold flex items-center gap-2 flex-wrap">
                  {g.name}
                  {pending && <span className="chip bg-cef-pink text-white text-[10px] py-0.5" data-testid={`lg-pending-${g.id}`}>EN ATTENTE</span>}
                  {g.province && <span className="chip bg-cef-green text-[10px] py-0.5">{g.province}</span>}
                  {g.submitter_email && <span className="chip bg-white text-[10px] py-0.5">soumis par {g.submitter_name || g.submitter_email}</span>}
                </div>
                {g.description && <p className="mt-1 text-sm text-neutral-700 line-clamp-2">{g.description}</p>}
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-neutral-600">
                  {g.city && <span>📍 {g.city}</span>}
                  {g.school && <span>🏫 {g.school}</span>}
                  {g.lat && <span>🗺️ géolocalisé</span>}
                  {g.instagram_url && <a href={g.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"><Instagram className="w-3 h-3" /> Insta</a>}
                  {g.contact_email && <a href={`mailto:${g.contact_email}`} className="inline-flex items-center gap-1 underline"><Mail className="w-3 h-3" /> Mail</a>}
                </div>
              </div>
              <div className="flex gap-2">
                {pending && <button onClick={() => approve(g)} className="neo-btn bg-cef-green text-sm py-2" data-testid={`approve-lg-${g.id}`}>✓ Approuver</button>}
                <button onClick={() => edit(g)} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`edit-lg-${g.id}`}>Éditer</button>
                <button onClick={() => del(g.id)} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid={`del-lg-${g.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
